import moment from "moment-mini";
import {Engine} from "platform/engine/Engine";
import {SelectDealPayload, SetPositionsOrdersPayload, SetTrades} from "core/redux/trades/TradesReduxActions";
import Platform from "platform/Platform";
import {StoreState} from "core/redux/StoreState";
import {TradeColumn} from "enum/TradeColumn";
import {SortDirection} from "platform/enum/SortDirection";
import Utils from "platform/util/Utils";
import {Trade} from "core/redux/trades/TradesReduxState";
import {Deal} from "platform/protocol/trading/Deal";
import {TradeUtil} from "core/util/TradeUtil";
import {Exposure} from "protocol/trade/Exposure";
import {TSMap} from "typescript-map";
import {PositionDirection} from "platform/protocol/enum/PositionDirection";
import {PortfolioType} from "enum/PortfolioType";
import {PortfolioTableState} from "core/redux/portfolio/PortfolioReduxState";
import {SetPortfolioFilterCounter, SetPortfolioTabPayload} from "core/redux/portfolio/PortfolioReduxActions";
import {ServiceType} from "enum/ServiceType";
import {AccountState} from "core/state/AccountState";
import {GetClosedPositionsByAccountIdsWithDateFilterRequest} from "protocol/trade/GetClosedPositionsByAccountIdsWithDateFilterRequest";
import {XhrUtil} from "core/util/XhrUtil";
import {SetClosedTrades, SetClosedTradesPending} from "core/redux/trades/ClosedTradesReduxActions";
import DateTimeFormat from "core/format/DateTimeFormat";
import {FilterDateState, FilterDateType} from "core/redux/filter/FilterReduxState";
import {FilterUtil} from "core/util/FilterUtil";
import {PortfolioDateFilterPayload, SetClosedTradesDefaultFilter, UpdatePortfolioDateFilter} from "core/redux/filter/FilterReduxActions";
import {SymbolsState} from "core/state/SymbolsState";
import {BIEventType} from "enum/BIEventType";

export const SortTrades = (column: TradeColumn, direction: SortDirection) => {
    return (t1: Trade, t2: Trade): number => {
        let result: number = 0;
        switch (column) {
            case TradeColumn.Symbol:
                result = Utils.compareString(t1.SymbolDisplayName, t2.SymbolDisplayName);
                break;
            case TradeColumn.OpenRate:
                result = Utils.compareNumber(t1.Rate, t2.Rate);
                break;
            case TradeColumn.Direction:
                result = Utils.compareNumber(t1.PositionDirections.length, t2.PositionDirections.length);
                if (result === 0) {
                    result = Utils.compareNumber(t1.PositionDirections[0], t2.PositionDirections[0]);
                }
                break;
            case TradeColumn.Order:
                result = Utils.compareNumber(t1.Amount, t2.Amount);
                break;
            case TradeColumn.MarketRate:
                result = Utils.compareNumber(t1.MarketRate, t2.MarketRate);
                break;
            case TradeColumn.TotalProfit:
                result = Utils.compareNumber(t1.PL, t2.PL);
                break;
            case TradeColumn.TakeProfit:
                result = Utils.compareNumber(t1.TakeProfitAmount || t1.TakeProfitRate, t2.TakeProfitAmount || t2.TakeProfitRate);
                break;
            case TradeColumn.StopLoss:
                result = Utils.compareNumber(t1.StopLossAmount || t1.StopLossRate, t2.StopLossAmount || t2.StopLossRate);
                break;
            case TradeColumn.CreatedDate:
                result = Utils.compareNumber(TradeUtil.createDate(t1), TradeUtil.createDate(t2));
                break;
        }
        return direction === SortDirection.Ask ? result : -result;
    };
};

export const SortDeals = (column: TradeColumn, direction: SortDirection) => {
    return (d1: Deal, d2: Deal): number => {
        let result: number = 0;
        switch (column) {
            case TradeColumn.Symbol:
                result = Utils.compareString(d1.SymbolDisplayName, d2.SymbolDisplayName);
                break;
            case TradeColumn.OpenRate:
                result = Utils.compareNumber(d1.Rate, d2.Rate);
                break;
            case TradeColumn.CloseRate:
                result = Utils.compareNumber(d1.CloseRate, d2.CloseRate);
                break;
            case TradeColumn.MarketRate:
                result = Utils.compareNumber(d1.MarketRate, d2.MarketRate);
                break;
            case TradeColumn.Direction:
                result = Utils.compareNumber(d1.PositionDirection, d2.PositionDirection);
                break;
            case TradeColumn.Order:
                result = Utils.compareNumber(d1.Amount, d2.Amount);
                break;
            case TradeColumn.TotalProfit:
                result = Utils.compareNumber(d1.PL, d2.PL);
                break;
            case TradeColumn.TakeProfit:
                result = Utils.compareNumber(d1.TakeProfitAmount || d1.TakeProfitRate, d2.TakeProfitAmount || d2.TakeProfitRate);
                break;
            case TradeColumn.StopLoss:
                result = Utils.compareNumber(d1.StopLossAmount || d1.StopLossRate, d2.StopLossAmount || d2.StopLossRate);
                break;
            case TradeColumn.CreatedDate:
                result = Utils.compareNumber(d1.Date, d2.Date);
                break;
            case TradeColumn.CloseDate:
                result = Utils.compareNumber(d1.CloseDate, d2.CloseDate);
                break;
        }
        return direction === SortDirection.Ask ? result : -result;
    };
};

export default class TradesEngine extends Engine {

    private static _instance: TradesEngine;

    public static instance(): TradesEngine {
        return this._instance || (this._instance = new this());
    }

    public onSetPositionsOrders = (payload: SetPositionsOrdersPayload): void => {
        const trades: Trade[] = [];
        const exposurePerSymbol: TSMap<number, Trade> = new TSMap();
        payload.Exposures.forEach((exposure: Exposure) => {
            if (exposure.OpenPositionsAmount > 1) {
                const trade: Trade = TradeUtil.mapExposure(exposure);
                trades.push(trade);
                exposurePerSymbol.set(exposure.SymbolId, trade);
            }
        });
        const storeState: StoreState = Platform.reduxState<StoreState>();
        const selectedDeals: TSMap<PortfolioType, Deal> = storeState.trades.selectedDeals.clone();
        const selectedPosition: Deal = selectedDeals.get(PortfolioType.OpenTrades);
        const selectedOrder: Deal = selectedDeals.get(PortfolioType.EntryOrders);
        const filterTrades: FilterDateState = storeState.filter.portfolioDateFilter.get(PortfolioType.OpenTrades);
        const filterOrders: FilterDateState = storeState.filter.portfolioDateFilter.get(PortfolioType.EntryOrders);
        let foundSelectedPosition: boolean;
        let foundSelectedOrder: boolean;
        let filterCountTrades: number = 0;
        let filterCountOrders: number = 0;
        const symbolIds: TSMap<number, any> = new TSMap();
        const OpenPositionsMap: {[key: number]: Deal} = {};
        const EntryOrdersMap: {[key: number]: Deal} = {};
        const EntryOrders: Deal[] = [];
        payload.EntryOrders.forEach((deal: Deal) => {
            EntryOrdersMap[deal.Id] = deal;
            if (FilterUtil.filterDeal(filterOrders, deal)) {
                filterCountOrders++;
                EntryOrders.push(deal);
            }
            symbolIds.set(deal.SymbolId, null);
            if (selectedOrder && selectedOrder.Id === deal.Id) {
                foundSelectedOrder = true;
            }
        });
        Platform.dispatch(SetPortfolioFilterCounter({
            portfolioType: PortfolioType.EntryOrders,
            value: filterCountOrders
        }));
        payload.OpenPositions.forEach((deal: Deal) => {
            OpenPositionsMap[deal.Id] = deal;
            if (FilterUtil.filterDeal(filterTrades, deal)) {
                filterCountTrades++;
            }
            symbolIds.set(deal.SymbolId, null);
            const trade: Trade = TradeUtil.mapDeal(deal);
            const exposure: Trade = exposurePerSymbol.get(deal.SymbolId);
            if (exposure) {
                exposure.trades.push(trade);
                if (exposure.PositionDirections.indexOf(deal.PositionDirection) < 0) {
                    exposure.PositionDirections.push(deal.PositionDirection);
                    if (exposure.PositionDirections.length > 1) {
                        exposure.PositionDirections = [PositionDirection.Buy, PositionDirection.Sell];
                    }
                }
            } else {
                trades.push(trade);
            }
            if (selectedPosition && selectedPosition.Id === deal.Id) {
                foundSelectedPosition = true;
            }
        });
        Platform.dispatch(SetPortfolioFilterCounter({
            portfolioType: PortfolioType.OpenTrades,
            value: filterCountTrades
        }));
        const symbolsState: SymbolsState = Platform.state(ServiceType.Symbols);
        symbolsState.symbolsDealIds = symbolIds.keys() || [];
        const {portfolio, settings} = Platform.reduxState<StoreState>();
        const {tables} = portfolio;
        const otState: PortfolioTableState = tables.get(PortfolioType.OpenTrades);
        const eoState: PortfolioTableState = tables.get(PortfolioType.EntryOrders);
        const otColumn: TradeColumn = otState.activeColumn;
        const otSort: SortDirection = otState.sort.get(otColumn);
        const filteredTrades: Trade[] = [];
        trades.forEach((t: Trade) => {
            const trade: Trade = FilterUtil.filterTrade(filterTrades, t);
            if (trade) {
                filteredTrades.push(trade);
            }
        });
        filteredTrades.sort(SortTrades(otColumn, otSort));
        exposurePerSymbol.values().forEach((trade: Trade) => {
            trade.trades.sort(SortTrades(otColumn, otSort));
        });
        payload.OpenPositions.sort(SortDeals(otColumn, otSort));
        EntryOrders.sort(SortDeals(eoState.activeColumn, eoState.sort.get(eoState.activeColumn)));
        if (settings.preferencesFetched) {
            if (!foundSelectedPosition) {
                selectedDeals.set(PortfolioType.OpenTrades, payload.OpenPositions[0]);
            }
            if (!foundSelectedOrder) {
                selectedDeals.set(PortfolioType.EntryOrders, EntryOrders[0]);
            }
        }
        Platform.dispatch(SetTrades({
            OpenPositionsMap,
            EntryOrdersMap,
            EntryOrders,
            trades: filteredTrades,
            selectedDeals,
            OverviewPositions: [...payload.OpenPositions].sort((d1: Deal, d2: Deal) => Utils.compareNumber(d2.Date, d1.Date))
        }));
    }

    public onSelectDeal = (payload: SelectDealPayload): void => {
        const {OpenPositionsMap, EntryOrdersMap} = Platform.reduxState<StoreState>().trades;
        const deals: {[key: number]: Deal} = payload.portfolioType === PortfolioType.OpenTrades ? OpenPositionsMap
            : payload.portfolioType === PortfolioType.EntryOrders ? EntryOrdersMap : null;
        if (deals) {
            const deal: Deal = deals[payload.id];
            if (deal) {
                Platform.bi().track(BIEventType.TradeSelected, {
                    SymbolName: deal.SymbolDisplayName,
                    DealId: deal.Id
                });
            }
        }
    }

    public onSetPortfolioTab = (payload: SetPortfolioTabPayload): void => {
        if (payload.portfolioType === PortfolioType.ClosedTrades) {
            this._logger.debug("Fetch closed trades");
            const filter: FilterDateState = Platform.reduxState<StoreState>().filter.portfolioDateFilter.get(PortfolioType.ClosedTrades);
            this.fetchClosedTrades(filter.dateType, filter.from, filter.to);
        }
    }

    public onSetPortfolioDateFilter = (payload: PortfolioDateFilterPayload): void => {
        if (payload.portfolioType === PortfolioType.ClosedTrades) {
            this._logger.debug("Set closed trades date filter to: " + payload.dateType);
            Platform.dispatch(SetClosedTradesPending({pending: true}));
            this.fetchClosedTradesInternal(payload.dateType, payload.from, payload.to).then(() => {
                Platform.dispatch(SetClosedTradesPending({pending: false}));
            }).catch(() => {
                this._logger.warn("Failed fetch closed trades on date filter change");
                Platform.dispatch(SetClosedTradesPending({pending: false}));
            });
        }
    }

    private fetchClosedTrades = (dateType: FilterDateType, from?: moment.Moment, to?: moment.Moment): void => {
        // NOTE: We will recalculate default filter each time user navigate to the closed trades tab
        // (because user can close trade during the session)
        Platform.dispatch(SetClosedTradesDefaultFilter({dateType}));
        Platform.dispatch(SetClosedTradesPending({pending: true}));
        this.fetchClosedTradesInternal(dateType, from, to).then((closedTrades: Deal[]) => {
            if (Utils.isArrayEmpty(closedTrades)) {
                const nextDateType: FilterDateType = FilterUtil.nextClosedTradesDateType(dateType);
                if (Utils.isNotNull(nextDateType)) {
                    this.fetchClosedTrades(nextDateType);
                } else {
                    Platform.dispatch(SetClosedTradesPending({pending: false}));
                }
            } else {
                Platform.dispatch(SetClosedTradesPending({pending: false}));
                Platform.dispatch(UpdatePortfolioDateFilter({
                    portfolioType: PortfolioType.ClosedTrades,
                    dateType,
                    from,
                    to
                }));
            }
        }).catch(() => {
            this._logger.warn("Failed fetch closed trades on switch to ClosedTrades portfolio tab. Date type: " + dateType);
            Platform.dispatch(SetClosedTradesPending({pending: false}));
        });
    }

    private fetchClosedTradesInternal = (dateType: FilterDateType, from: moment.Moment, to: moment.Moment): Promise<Deal[]> => {
        return new Promise<Deal[]>((resolve, reject) => {
            this._logger.debug("Fetch closed trades");
            const accountState: AccountState = Platform.state(ServiceType.Account);
            const request: GetClosedPositionsByAccountIdsWithDateFilterRequest = {
                AccountIds: [accountState.accountId]
            };
            const range: { from: Date, to: Date } = FilterUtil.getDateRange(dateType, from, to);
            request.CloseDateFrom = dateType === FilterDateType.All ? null : DateTimeFormat.formatServerDate(range.from);
            request.ClosedDateTo = DateTimeFormat.formatServerDate(range.to);
            // NOTE: Possible to fetch closed trades by trade open date. Not send both open/close dates filter at the same time
            // request.OpenDateFrom = DateTimeFormat.formatServerDate(range.from);
            // request.OpenDateTo = DateTimeFormat.formatServerDate(range.to);
            this._logger.debug("Get closed deals with params: " + JSON.stringify(request));
            XhrUtil.sendAuthenticated(request, "WebProfitServer/AccountStatesService.svc/json/GetClosedPositionsByAccountIdsWithDateFilter", null, true)
                .then((ClosedTrades: Deal[]) => {
                    const {tables} = Platform.reduxState<StoreState>().portfolio;
                    const ctState: PortfolioTableState = tables.get(PortfolioType.ClosedTrades);
                    ClosedTrades?.sort(SortDeals(ctState.activeColumn, ctState.sort.get(ctState.activeColumn)));
                    Platform.dispatch(SetClosedTrades({ClosedTrades}));
                    resolve(ClosedTrades);
                })
                .catch(reject);
        });
    }
}
